import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAssets, createAsset, updateAsset, deleteAsset, getUsers, getSites, createSite, createServiceRequest, uploadAssetPhoto, deleteAssetPhoto, fileUrl, listTaxonomy } from '../../services/api';
import { TaxonomyPicker } from '../../components/TaxonomyPicker';
import { PageHeader, StatusBadge } from '../../components/Shared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { UkAddressFields, formatAddress } from '../../components/UkAddressFields';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Plus, MapPin, Calendar, AlertCircle, Pencil, Trash2, Building2, Search, Hash, ChevronDown, ChevronRight, AlertTriangle, Camera, ImageIcon, X, CornerDownRight, GitBranch } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { SiteMapPicker } from '../../components/SiteMapPicker';
import { BulkImportAssetsDialog } from '../../components/BulkImportAssetsDialog';

// Default seeds — used as a fallback if the taxonomy endpoint hasn't loaded
// yet, and as the initial render of the type filter on first paint. The
// admin can edit the live list at /admin/settings/lists.
const ASSET_TYPES_FALLBACK = ['Mechanical', 'Electrical', 'Hydraulic', 'HVAC', 'Plumbing', 'Safety', 'IT', 'Other'];
const ASSET_STATUS = ['operational', 'maintenance', 'down', 'retired'];
const CRITICALITY = ['low', 'medium', 'high', 'critical'];
const CRITICALITY_COLORS = {
  low: 'text-slate-600', medium: 'text-blue-700',
  high: 'text-amber-600', critical: 'text-red-600',
};

const emptyForm = {
  name: '', location: '', type: 'Mechanical',
  status: 'operational', criticality: 'medium',
  customerId: '', siteId: '', parentAssetId: '',
  serialNumber: '', notes: '',
};

const Assets = () => {
  const { user } = useAuth();
  const role = user?.role || 'admin';
  const isAdmin = role === 'admin';
  const isCustomer = role === 'customer';
  const canEdit = isAdmin;

  const [assets, setAssets] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [collapsed, setCollapsed] = useState({});
  // Asset types from the admin-managed taxonomy (with hardcoded fallback so
  // first paint is never empty if the fetch is in flight or fails).
  const [assetTypes, setAssetTypes] = useState(
    ASSET_TYPES_FALLBACK.map((t, i) => ({ id: `seed-${i}`, label: t })),
  );
  useEffect(() => {
    listTaxonomy('asset_type').then(setAssetTypes).catch(() => { /* keep fallback */ });
  }, []);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // Bulk-import CSV dialog (admin only)
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Quick-add site dialog
  const [siteOpen, setSiteOpen] = useState(false);
  const [siteForm, setSiteForm] = useState({ name: '', address: '', customerId: '', lat: null, lng: null, geofenceMeters: 250 });

  // Customer "Report an issue" dialog
  const navigate = useNavigate();
  const [reportAsset, setReportAsset] = useState(null);
  const [reportForm, setReportForm] = useState({ title: '', description: '', priority: 'medium' });
  const [reportSubmitting, setReportSubmitting] = useState(false);

  const openReport = (asset) => {
    setReportAsset(asset);
    setReportForm({
      title: `Issue with ${asset.name}`,
      description: '',
      priority: 'medium',
    });
  };

  const submitReport = async () => {
    if (!reportForm.title.trim()) return toast.error('Please describe the issue');
    setReportSubmitting(true);
    try {
      await createServiceRequest({
        title: reportForm.title.trim(),
        description: reportForm.description.trim(),
        priority: reportForm.priority,
        assetId: reportAsset.id,
        siteId: reportAsset.siteId || undefined,
      });
      toast.success('Work request submitted — our team has been notified');
      setReportAsset(null);
      navigate('/customer/requests');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to submit');
    } finally {
      setReportSubmitting(false);
    }
  };

  const refresh = async () => {
    // Customers can't list users; only fetch what's accessible per role.
    const tasks = [getAssets(), getSites()];
    if (!isCustomer) tasks.push(getUsers());
    const [a, s, u] = await Promise.all(tasks);
    setAssets(a);
    setSites(s || []);
    if (u) setCustomers(u.filter(usr => usr.role === 'customer'));
  };

  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  const sitesForCustomer = (customerId) =>
    customerId ? sites.filter(s => s.customerId === customerId) : sites;

  /**
   * Valid parent-asset candidates for the current form.
   *
   * Rules mirror the backend:
   *   · never self
   *   · never a descendant of the asset being edited (would create a cycle)
   *   · if a customer is selected, limit to that customer's assets
   *     (and allow unassigned parents for backwards compat)
   */
  const parentAssetOptions = useMemo(() => {
    const editingId = editing?.id;
    // Build an id→children map so we can prune descendants when editing.
    const byParent = new Map();
    for (const a of assets) {
      if (!a.parentAssetId) continue;
      if (!byParent.has(a.parentAssetId)) byParent.set(a.parentAssetId, []);
      byParent.get(a.parentAssetId).push(a.id);
    }
    const descendants = new Set();
    if (editingId) {
      const stack = [editingId];
      while (stack.length) {
        const cur = stack.pop();
        descendants.add(cur);
        for (const c of (byParent.get(cur) || [])) stack.push(c);
      }
    }
    return assets
      .filter((a) => a.id !== editingId && !descendants.has(a.id))
      .filter((a) => !form.customerId || !a.customerId || a.customerId === form.customerId)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [assets, editing, form.customerId]);

  const openCreate = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  /** Start a new asset pre-linked to a parent ("+ Add sub-asset"). */
  const openCreateChild = (parent) => {
    setEditing(null);
    setForm({
      ...emptyForm,
      parentAssetId: parent.id,
      customerId: parent.customerId || '',
      siteId: parent.siteId || '',
      // Inherit type so the admin can tweak, not re-enter.
      type: parent.type || emptyForm.type,
      location: parent.location || '',
    });
    setOpen(true);
  };
  const openEdit = (a) => {
    setEditing(a);
    setForm({
      name: a.name || '', location: a.location || '',
      type: a.type || 'Mechanical', status: a.status || 'operational',
      criticality: a.criticality || 'medium',
      customerId: a.customerId || '', siteId: a.siteId || '',
      parentAssetId: a.parentAssetId || '',
      serialNumber: a.serialNumber || '', notes: a.notes || '',
    });
    setOpen(true);
  };

  // Asset photo upload — only relevant when editing an existing asset
  // (photos hang off the asset doc, so we need an id to attach to).
  const [photoUploading, setPhotoUploading] = useState(false);
  const handlePhotoUpload = async (file) => {
    if (!editing || !file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please pick an image file');
      return;
    }
    setPhotoUploading(true);
    try {
      const info = await uploadAssetPhoto(editing.id, file);
      setEditing({ ...editing, photoUrl: info.url, photo: info });
      setAssets((list) => list.map(a =>
        a.id === editing.id ? { ...a, photoUrl: info.url, photo: info } : a
      ));
      toast.success('Photo updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setPhotoUploading(false);
    }
  };
  const handlePhotoRemove = async () => {
    if (!editing) return;
    setPhotoUploading(true);
    try {
      await deleteAssetPhoto(editing.id);
      setEditing({ ...editing, photoUrl: undefined, photo: undefined });
      setAssets((list) => list.map(a =>
        a.id === editing.id ? { ...a, photoUrl: undefined, photo: undefined } : a
      ));
      toast.success('Photo removed');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Remove failed');
    } finally {
      setPhotoUploading(false);
    }
  };


  const save = async () => {
    if (!form.name.trim() || !form.location.trim()) {
      return toast.error('Asset name and location are required');
    }
    setSaving(true);
    try {
      const payload = { ...form };
      // Don't send empty strings for optional IDs
      if (!payload.customerId) delete payload.customerId;
      if (!payload.siteId) delete payload.siteId;
      if (!payload.parentAssetId) delete payload.parentAssetId;
      if (editing) await updateAsset(editing.id, payload);
      else await createAsset(payload);
      toast.success(editing ? 'Asset updated' : 'Asset added');
      setOpen(false);
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Delete asset "${a.name}"? This cannot be undone.`)) return;
    try {
      await deleteAsset(a.id);
      toast.success('Asset deleted');
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Delete failed');
    }
  };

  const saveSite = async () => {
    if (!siteForm.name.trim()) return toast.error('Site name required');
    try {
      const newSite = await createSite(siteForm);
      toast.success('Site added');
      setSites([...sites, newSite]);
      setForm(f => ({ ...f, siteId: newSite.id, customerId: newSite.customerId || f.customerId }));
      setSiteOpen(false);
      setSiteForm({ name: '', address: '', customerId: '', lat: null, lng: null, geofenceMeters: 250 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add site');
    }
  };

  const filtered = useMemo(() => {
    let list = assets;
    if (filter === 'mine-unassigned') list = list.filter(a => !a.customerId);
    else if (filter !== 'all') list = list.filter(a => a.status === filter);
    if (search.trim()) {
      const q = search.toLowerCase();
      const direct = list.filter(a =>
        a.name.toLowerCase().includes(q) ||
        (a.location || '').toLowerCase().includes(q) ||
        (a.customerName || '').toLowerCase().includes(q) ||
        (a.serialNumber || '').toLowerCase().includes(q) ||
        (a.id || '').toLowerCase().includes(q) ||
        (a.parentAssetName || '').toLowerCase().includes(q)
      );
      // When a hit happens anywhere in the tree, pull its ancestors
      // and descendants along so the card + inline children render
      // as one coherent unit. Without this, searching for the parent
      // name drops the children (and vice versa).
      const byId = new Map(list.map((a) => [a.id, a]));
      const byParent = new Map();
      for (const a of list) {
        if (!a.parentAssetId) continue;
        if (!byParent.has(a.parentAssetId)) byParent.set(a.parentAssetId, []);
        byParent.get(a.parentAssetId).push(a.id);
      }
      const keep = new Set();
      for (const a of direct) {
        // Walk up to the root.
        let cur = a;
        while (cur) {
          if (keep.has(cur.id)) break;
          keep.add(cur.id);
          cur = cur.parentAssetId ? byId.get(cur.parentAssetId) : null;
        }
        // Walk down every descendant.
        const stack = [a.id];
        while (stack.length) {
          const id = stack.pop();
          for (const kid of (byParent.get(id) || [])) {
            if (!keep.has(kid)) { keep.add(kid); stack.push(kid); }
          }
        }
      }
      list = list.filter((a) => keep.has(a.id));
    }
    return list;
  }, [assets, filter, search]);

  // Build a child → parent lookup so we can roll sub-customer assets
  // up into their parent customer's group. Sub-customers still show
  // as a sub-heading inside the parent group so the admin sees who
  // owns which assets, but they no longer clutter the top-level list
  // with a separate section per sub-customer.
  const parentByChild = useMemo(() => {
    const map = {};
    (customers || []).forEach(c => {
      if (c.parentCustomerId) map[c.id] = c.parentCustomerId;
    });
    return map;
  }, [customers]);
  const customerNameById = useMemo(() => {
    const map = {};
    (customers || []).forEach(c => {
      map[c.id] = c.company || c.name || '';
    });
    return map;
  }, [customers]);
  // Group assets by customer (or by site for customer role) AND separate
  // top-level from child assets so children render tucked inside their
  // parent's card instead of mixed into the grid. Orphans (child whose
  // parent isn't in the current filtered view) are promoted to
  // top-level so they're never silently hidden. For admins, sub-customer
  // assets roll up under the parent customer group but stay separated
  // by a sub-heading so ownership is still explicit.
  const groups = useMemo(() => {
    const byKey = new Map();
    const visibleIds = new Set(filtered.map((a) => a.id));
    for (const a of filtered) {
      // Roll sub-customer assets up under the parent customer.
      const rolledCustomerId = parentByChild[a.customerId] || a.customerId;
      const key = isCustomer
        ? (a.siteId || '__nosite__')
        : (rolledCustomerId || '__unassigned__');
      const rolledLabel = customerNameById[rolledCustomerId] || a.customerName || 'Unassigned';
      const label = isCustomer
        ? (a.siteName || 'No site')
        : rolledLabel;
      if (!byKey.has(key)) {
        byKey.set(key, {
          label,
          rolledCustomerId,
          tops: [],
          childrenByParent: new Map(),
          // sub-customer bucketing (admins only). key = subCustomerId
          // or '__self' for assets owned directly by the parent.
          subBuckets: new Map(),
        });
      }
      const bucket = byKey.get(key);
      if (a.parentAssetId && visibleIds.has(a.parentAssetId)) {
        if (!bucket.childrenByParent.has(a.parentAssetId)) {
          bucket.childrenByParent.set(a.parentAssetId, []);
        }
        bucket.childrenByParent.get(a.parentAssetId).push(a);
      } else {
        bucket.tops.push(a);
      }
      if (!isCustomer) {
        const isSub = a.customerId && a.customerId !== rolledCustomerId;
        const subKey = isSub ? a.customerId : '__self';
        if (!bucket.subBuckets.has(subKey)) {
          const subLabel = isSub
            ? (customerNameById[a.customerId] || a.customerName || 'Sub-customer')
            : rolledLabel;
          bucket.subBuckets.set(subKey, { label: subLabel, isSub, assets: [] });
        }
        // Only include top-level assets in the sub-bucket lists —
        // children still render tucked inside their parent card
        // regardless of which sub-customer owns them.
        if (!(a.parentAssetId && visibleIds.has(a.parentAssetId))) {
          bucket.subBuckets.get(subKey).assets.push(a);
        }
      }
    }
    // Stable sort: real names alphabetical; "Unassigned"/"No site" last.
    return Array.from(byKey.values()).sort((a, b) => {
      const tail = ['Unassigned', 'No site'];
      const ai = tail.indexOf(a.label);
      const bi = tail.indexOf(b.label);
      if (ai !== -1 && bi === -1) return 1;
      if (bi !== -1 && ai === -1) return -1;
      return a.label.localeCompare(b.label);
    });
  }, [filtered, isCustomer, parentByChild, customerNameById]);

  // Expansion state for parent-asset cards (collapsed by default; click
  // the chevron in the parent card footer to show its children inline).
  const [expandedAssets, setExpandedAssets] = useState(() => new Set());
  const toggleAssetExpanded = (id) =>
    setExpandedAssets((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const counts = {
    all: assets.length,
    operational: assets.filter(a => a.status === 'operational').length,
    maintenance: assets.filter(a => a.status === 'maintenance').length,
    down: assets.filter(a => a.status === 'down').length,
    'mine-unassigned': assets.filter(a => !a.customerId).length,
  };

  if (loading) return <div className="p-8 text-slate-500">Loading assets...</div>;

  const groupLabel = isCustomer ? 'Site' : 'Customer';

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <PageHeader
        title="Assets"
        description={
          isCustomer
            ? 'All equipment we maintain at your sites.'
            : isAdmin
              ? 'Track, maintain, and monitor every piece of critical equipment — grouped by customer.'
              : 'Every asset across all customers — grouped by customer.'
        }
        actions={
          canEdit ? (
            <div className="flex flex-wrap items-center gap-2">
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => setBulkImportOpen(true)}
                  data-testid="bulk-import-assets-btn"
                >
                  Import CSV
                </Button>
              )}
              <Button onClick={openCreate} className="bg-slate-900 hover:bg-slate-800" data-testid="add-asset-btn">
                <Plus className="h-4 w-4 mr-2" /> Add Asset
              </Button>
            </div>
          ) : null
        }
      />

      {/* Filter tabs + search */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        {[
          { key: 'all', label: 'All' },
          { key: 'operational', label: 'Operational' },
          { key: 'maintenance', label: 'Maintenance' },
          { key: 'down', label: 'Down' },
          ...(isCustomer ? [] : [{ key: 'mine-unassigned', label: 'Unassigned' }]),
        ].map(t => (
          <button
            key={t.key}
            onClick={() => setFilter(t.key)}
            data-testid={`asset-filter-${t.key}`}
            className={`px-3 py-1.5 rounded-md font-medium transition-colors ${
              filter === t.key
                ? 'bg-slate-900 text-white'
                : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
            }`}
          >
            {t.label} <span className="text-xs opacity-70">({counts[t.key]})</span>
          </button>
        ))}
        <div className="relative flex-1 min-w-[240px] ml-auto">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            data-testid="asset-search-input"
            placeholder={isCustomer
              ? 'Search by name, location, serial...'
              : 'Search by name, location, customer, serial...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9 bg-white"
          />
        </div>
      </div>

      {groups.length === 0 && (
        <div className="p-12 text-center text-slate-500 bg-white border border-dashed border-slate-200 rounded-md">
          {assets.length === 0
            ? (canEdit
              ? 'No assets yet. Click "Add Asset" to create your first one.'
              : 'No assets recorded yet.')
            : 'No assets match your filter.'}
        </div>
      )}

      {groups.length > 1 && (
        <div className="flex items-center justify-end gap-3 mb-3 -mt-2">
          <button
            type="button"
            onClick={() => setCollapsed(Object.fromEntries(groups.map(g => [g.label, false])))}
            data-testid="asset-groups-expand-all"
            className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900"
          >
            Expand all
          </button>
          <span className="text-slate-300">·</span>
          <button
            type="button"
            onClick={() => setCollapsed(Object.fromEntries(groups.map(g => [g.label, true])))}
            data-testid="asset-groups-collapse-all"
            className="text-xs uppercase tracking-wider text-slate-500 hover:text-slate-900"
          >
            Collapse all
          </button>
        </div>
      )}

      <div className="space-y-8">
        {groups.map(({ label: groupName, tops: groupTops, childrenByParent, subBuckets }) => {
          // Groups are collapsed by default — admins typically have
          // dozens of customers and only care about a couple at a time.
          // While a search query is active we force-expand so results
          // are never hidden behind a closed header.
          const userCollapsed = collapsed[groupName] !== false;
          const isCollapsed = search.trim() ? false : userCollapsed;
          const totalAssets = groupTops.length +
            Array.from(childrenByParent.values()).reduce((n, arr) => n + arr.length, 0);
          return (
            <section key={groupName} data-testid={`asset-group-${groupName.replace(/\s+/g, '-').toLowerCase()}`}>
              <button
                type="button"
                onClick={() => setCollapsed(c => ({ ...c, [groupName]: !userCollapsed }))}
                className="w-full flex items-center justify-between mb-3 group"
                data-testid={`asset-group-toggle-${groupName.replace(/\s+/g, '-').toLowerCase()}`}
              >
                <div className="flex items-center gap-3">
                  {isCollapsed
                    ? <ChevronRight className="h-4 w-4 text-slate-400" />
                    : <ChevronDown className="h-4 w-4 text-slate-400" />}
                  <Building2 className="h-4 w-4 text-blue-700" />
                  <h2 className="font-display uppercase tracking-wide text-slate-900 text-base">
                    {groupName}
                  </h2>
                  <span className="text-xs text-slate-500 font-mono">
                    {totalAssets} {totalAssets === 1 ? 'asset' : 'assets'}
                  </span>
                </div>
                <span className="text-xs uppercase tracking-wider text-slate-400 group-hover:text-slate-600">
                  {groupLabel}
                </span>
              </button>
              {!isCollapsed && (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Render each sub-bucket (parent's own + one per
                      sub-customer) in a single grid, inserting a
                      full-width heading row between buckets when the
                      group contains at least one sub-customer. This
                      keeps sub-customer ownership visible without
                      breaking the existing card layout. */}
                  {(() => {
                    // Build the flat render list.
                    const items = [];
                    const hasAnyAsset = groupTops.length > 0 ||
                      Array.from(childrenByParent.values()).some(a => a.length);
                    if (isCustomer || !hasAnyAsset || !subBuckets) {
                      // Customer role or empty group — no sub-bucket
                      // headings, keep the flat list of tops.
                      items.push(...groupTops.map(a => ({ kind: 'asset', a })));
                    } else {
                      // Order: parent-owned first, then sub-customers
                      // alphabetically.
                      const subList = Array.from(subBuckets.values()).sort((x, y) => {
                        if (x.isSub !== y.isSub) return x.isSub ? 1 : -1;
                        return (x.label || '').localeCompare(y.label || '');
                      });
                      const hasSub = subList.some(b => b.isSub);
                      subList.forEach((bucket, idx) => {
                        if (hasSub) {
                          items.push({
                            kind: 'header',
                            label: bucket.isSub ? `Sub-customer: ${bucket.label}` : `${bucket.label} (direct)`,
                            isSub: bucket.isSub,
                            key: `hdr-${idx}-${bucket.label}`,
                          });
                        }
                        bucket.assets.forEach(a => items.push({ kind: 'asset', a }));
                      });
                    }
                    return items.map((item) => {
                      if (item.kind === 'header') {
                        return (
                          <div
                            key={item.key}
                            className={`col-span-full flex items-center gap-2 pt-2 ${item.isSub ? 'mt-2' : ''}`}
                            data-testid={`asset-sub-header-${item.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}
                          >
                            <span className={`h-1.5 w-1.5 rounded-full ${item.isSub ? 'bg-blue-600' : 'bg-slate-400'}`} />
                            <span className={`text-xs uppercase tracking-wider font-semibold ${item.isSub ? 'text-blue-700' : 'text-slate-600'}`}>
                              {item.label}
                            </span>
                            <span className="flex-1 h-px bg-slate-100" />
                          </div>
                        );
                      }
                      const a = item.a;
                      const children = childrenByParent.get(a.id) || [];
                      const hasChildren = children.length > 0;
                      const isExpanded = expandedAssets.has(a.id);
                      return (
                      <div key={a.id} data-testid={`asset-card-${a.id}`} className="bg-white border border-slate-200 rounded-md group hover:border-blue-700 hover:shadow-md transition-all overflow-hidden">
                      {a.photoUrl ? (
                        <div className="aspect-[16/9] bg-slate-100 relative overflow-hidden">
                          <img
                            src={fileUrl(a.photoUrl)}
                            alt={a.name}
                            className="absolute inset-0 w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ) : canEdit ? (
                        <button
                          type="button"
                          onClick={() => openEdit(a)}
                          className="aspect-[16/9] bg-slate-50 flex flex-col items-center justify-center text-slate-400 hover:text-blue-700 hover:bg-blue-50 transition-colors w-full"
                          data-testid={`asset-photo-empty-${a.id}`}
                        >
                          <Camera className="h-7 w-7 mb-1" />
                          <span className="text-xs uppercase tracking-wider">Add photo</span>
                        </button>
                      ) : null}
                      <div className="p-5">
                      <div className="flex items-start justify-between mb-4">
                        <div className="min-w-0">
                          <div className="text-xs font-mono text-slate-500">{a.id}</div>
                          <h3 className="font-display uppercase text-lg text-slate-900 tracking-wide mt-0.5 truncate">{a.name}</h3>
                          {a.parentAssetName && (
                            <div className="mt-1 inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-slate-500">
                              <CornerDownRight className="h-3 w-3" />
                              <span className="truncate" data-testid={`asset-parent-${a.id}`}>under {a.parentAssetName}</span>
                            </div>
                          )}
                        </div>
                        <div className="flex items-start gap-1 shrink-0">
                          <StatusBadge status={a.status} />
                        </div>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex items-center gap-2 text-slate-600">
                          <MapPin className="h-4 w-4 shrink-0" />
                          <span className="truncate">{a.location}{a.siteName ? ` · ${a.siteName}` : ''}</span>
                        </div>
                        {!isCustomer && a.customerName && (
                          <div className="flex items-center gap-2 text-slate-600">
                            <Building2 className="h-4 w-4 shrink-0" />
                            <span className="truncate">{a.customerName}</span>
                          </div>
                        )}
                        {a.serialNumber && (
                          <div className="flex items-center gap-2 text-slate-500 text-xs">
                            <Hash className="h-3 w-3 shrink-0" /> {a.serialNumber}
                          </div>
                        )}
                        <div className="flex items-center gap-2 text-slate-600">
                          <Calendar className="h-4 w-4 shrink-0" /> Last maintained: {a.lastMaintenance || '—'}
                        </div>
                        <div className="flex items-center gap-2 text-slate-600">
                          <AlertCircle className="h-4 w-4 shrink-0" /> Criticality:
                          <span className={`font-semibold capitalize ${CRITICALITY_COLORS[a.criticality] || 'text-slate-700'}`}>
                            {a.criticality}
                          </span>
                        </div>
                      </div>
                      <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between gap-2">
                        <span className="text-xs text-slate-500 shrink-0">{a.type}</span>
                        {isCustomer && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openReport(a)}
                            data-testid={`report-asset-${a.id}`}
                            className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50 hover:text-red-800 hover:border-red-300"
                          >
                            <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Report an issue
                          </Button>
                        )}
                        {canEdit && (
                          <div className="flex gap-1 shrink-0">
                            <Button
                              size="icon" variant="ghost" className="h-7 w-7 text-blue-700"
                              onClick={() => openCreateChild(a)}
                              title="Add sub-asset"
                              data-testid={`add-subasset-${a.id}`}
                            >
                              <GitBranch className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`edit-asset-${a.id}`}>
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(a)} data-testid={`delete-asset-${a.id}`}>
                              <Trash2 className="h-3.5 w-3.5 text-red-600" />
                            </Button>
                          </div>
                        )}
                      </div>
                      {hasChildren && (
                        <button
                          type="button"
                          onClick={() => toggleAssetExpanded(a.id)}
                          className="mt-3 -mx-5 -mb-5 px-5 py-2 flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-slate-600 hover:bg-slate-50 border-t border-slate-100"
                          data-testid={`asset-toggle-children-${a.id}`}
                        >
                          <span className="flex items-center gap-1.5">
                            <GitBranch className="h-3.5 w-3.5 text-blue-700" />
                            {children.length} sub-asset{children.length === 1 ? '' : 's'}
                          </span>
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5" />
                            : <ChevronRight className="h-3.5 w-3.5" />}
                        </button>
                      )}
                      </div>
                      {hasChildren && isExpanded && (
                        <div className="bg-slate-50 border-t border-slate-200 divide-y divide-slate-100" data-testid={`asset-children-${a.id}`}>
                          {children.map((c) => (
                            <div key={c.id} className="flex items-center gap-2 px-5 py-2 text-sm" data-testid={`asset-child-row-${c.id}`}>
                              <CornerDownRight className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                              <span className="text-xs font-mono text-slate-500 shrink-0">{c.id}</span>
                              <span className="truncate flex-1 text-slate-800">{c.name}</span>
                              <span className="shrink-0"><StatusBadge status={c.status} /></span>
                              {canEdit && (
                                <div className="flex gap-0.5 shrink-0">
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => openEdit(c)} data-testid={`edit-asset-${c.id}`}>
                                    <Pencil className="h-3 w-3" />
                                  </Button>
                                  <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => remove(c)} data-testid={`delete-asset-${c.id}`}>
                                    <Trash2 className="h-3 w-3 text-red-600" />
                                  </Button>
                                </div>
                              )}
                              {isCustomer && (
                                <Button size="sm" variant="outline" onClick={() => openReport(c)} className="h-6 text-[11px] border-red-200 text-red-700 hover:bg-red-50" data-testid={`report-asset-${c.id}`}>
                                  Report
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    );
                    });
                  })()}
                </div>
              )}
            </section>
          );
        })}
      </div>

      {/* Asset dialog (admin only) */}
      {canEdit && (
      <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? `Edit ${editing.name}` : 'Add Asset'}</DialogTitle>
            <DialogDescription>
              Give the asset a clear name, its physical location, and optionally link it to a customer and/or site.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Photo (only available after the asset is created — needs an id) */}
            {editing && (
              <div>
                <Label>Photo</Label>
                <div className="mt-1.5">
                  {editing.photoUrl ? (
                    <div className="relative aspect-[16/9] bg-slate-100 rounded-md overflow-hidden border border-slate-200 group">
                      <img
                        src={fileUrl(editing.photoUrl)}
                        alt={editing.name}
                        className="absolute inset-0 w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-1.5">
                        <label className="cursor-pointer bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1.5 hover:bg-white shadow-sm" title="Replace photo">
                          <Camera className="h-4 w-4 text-slate-700" />
                          <input
                            type="file"
                            accept="image/*"
                            className="hidden"
                            onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                            disabled={photoUploading}
                            data-testid="replace-asset-photo-input"
                          />
                        </label>
                        <button
                          type="button"
                          onClick={handlePhotoRemove}
                          disabled={photoUploading}
                          className="bg-white/95 backdrop-blur border border-slate-200 rounded-md p-1.5 hover:bg-rose-50 hover:border-rose-200 shadow-sm"
                          title="Remove photo"
                          data-testid="remove-asset-photo-btn"
                        >
                          <X className="h-4 w-4 text-rose-600" />
                        </button>
                      </div>
                    </div>
                  ) : (
                    <label
                      className="flex flex-col items-center justify-center aspect-[16/9] bg-slate-50 border-2 border-dashed border-slate-200 rounded-md hover:border-blue-700 hover:bg-blue-50 transition-colors cursor-pointer"
                      data-testid="upload-asset-photo-label"
                    >
                      <Camera className="h-7 w-7 text-slate-400 mb-1" />
                      <span className="text-sm text-slate-600">{photoUploading ? 'Uploading...' : 'Tap to add a photo'}</span>
                      <span className="text-xs text-slate-400 mt-0.5">JPG, PNG, GIF or WebP · max 10 MB</span>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handlePhotoUpload(e.target.files?.[0])}
                        disabled={photoUploading}
                        data-testid="upload-asset-photo-input"
                      />
                    </label>
                  )}
                </div>
              </div>
            )}
            {!editing && (
              <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-md px-3 py-2">
                <Camera className="inline h-3.5 w-3.5 mr-1 -mt-0.5 text-slate-400" />
                Save the asset first, then come back to add a photo.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Asset name *</Label>
                <Input data-testid="asset-name-input" className="mt-1.5" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Conveyor Belt Line 3" />
              </div>
              <div>
                <Label>Type</Label>
                <TaxonomyPicker
                  kind="asset_type"
                  value={form.type}
                  onChange={(v) => setForm({ ...form, type: v || 'Mechanical' })}
                  placeholder="Select a type…"
                  testId="asset-type-picker"
                />
              </div>
            </div>

            <div>
              <Label>Physical location *</Label>
              <Input data-testid="asset-location-input" className="mt-1.5" value={form.location} onChange={e => setForm({ ...form, location: e.target.value })} placeholder="e.g. Plant Floor 2, Bay 4" />
              <p className="text-xs text-slate-500 mt-1">Free text — where this asset physically lives.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label>Customer</Label>
                <Select value={form.customerId || 'none'} onValueChange={v => setForm({ ...form, customerId: v === 'none' ? '' : v, siteId: '' })}>
                  <SelectTrigger className="mt-1.5" data-testid="asset-customer-select"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Unassigned —</SelectItem>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {customers.length === 0 && (
                  <p className="text-xs text-amber-700 mt-1">No customers yet. Add one on the Users page.</p>
                )}
              </div>
              <div>
                <Label className="flex items-center justify-between">
                  Site
                  <button
                    type="button"
                    onClick={() => { setSiteForm({ name: '', address: '', customerId: form.customerId, lat: null, lng: null, geofenceMeters: 250 }); setSiteOpen(true); }}
                    className="text-xs font-normal text-blue-700 hover:text-blue-900"
                    data-testid="quick-add-site-btn"
                  >
                    + New site
                  </button>
                </Label>
                <Select value={form.siteId || 'none'} onValueChange={v => setForm({ ...form, siteId: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1.5" data-testid="asset-site-select"><SelectValue placeholder="Select site..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— None —</SelectItem>
                    {sitesForCustomer(form.customerId).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}{s.address ? ` · ${s.address}` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  {form.customerId ? 'Showing sites for this customer.' : 'Select a customer first, or leave blank.'}
                </p>
              </div>
            </div>

            <div>
              <Label>Parent asset</Label>
              <Select
                value={form.parentAssetId || 'none'}
                onValueChange={v => setForm({ ...form, parentAssetId: v === 'none' ? '' : v })}
              >
                <SelectTrigger className="mt-1.5" data-testid="asset-parent-select"><SelectValue placeholder="Select a parent…" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None (top-level asset) —</SelectItem>
                  {parentAssetOptions.map(a => (
                    <SelectItem key={a.id} value={a.id}>
                      {a.name}{a.customerName ? ` · ${a.customerName}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-slate-500 mt-1">
                Optional — nest this under an existing asset (e.g. "Take-up" under "Extrusion line").
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <Label>Status</Label>
                <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{ASSET_STATUS.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Criticality</Label>
                <Select value={form.criticality} onValueChange={v => setForm({ ...form, criticality: v })}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{CRITICALITY.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Serial number</Label>
                <Input className="mt-1.5" value={form.serialNumber} onChange={e => setForm({ ...form, serialNumber: e.target.value })} placeholder="SN-123456" />
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Textarea className="mt-1.5" rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Any maintenance notes, quirks, or history" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-slate-900 hover:bg-slate-800" data-testid="save-asset-btn">
              {saving ? 'Saving...' : editing ? 'Save changes' : 'Add Asset'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick-add site dialog */}
      <Dialog open={siteOpen} onOpenChange={setSiteOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add a site</DialogTitle>
            <DialogDescription>Sites group assets together for a customer (e.g. "Manchester Factory", "Leeds Warehouse").</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Site name *</Label>
              <Input className="mt-1.5" value={siteForm.name} onChange={e => setSiteForm({ ...siteForm, name: e.target.value })} placeholder="e.g. Manchester Factory" data-testid="new-site-name-input" />
            </div>
            <div>
              <Label>Address</Label>
              <div className="mt-1.5">
                <UkAddressFields
                  value={siteForm.addressParts}
                  legacyText={siteForm.address}
                  onChange={(parts, picked) => setSiteForm((f) => ({
                    ...f, addressParts: parts, address: formatAddress(parts, ', '),
                    lat: f.lat == null && picked?.lat != null ? picked.lat : f.lat,
                    lng: f.lng == null && picked?.lng != null ? picked.lng : f.lng,
                  }))}
                  testId="asset-new-site-address"
                />
              </div>
            </div>
            {/* Geofencing for clock-in. Both lat AND lng must be set for
                geofencing to activate; otherwise engineers can clock in
                from anywhere. The map below lets admins drop a pin
                directly — the lat/lng inputs stay editable for paste-in
                workflows (e.g. from a Google Maps "share location" link). */}
            <SiteMapPicker
              lat={siteForm.lat}
              lng={siteForm.lng}
              radiusMeters={siteForm.geofenceMeters}
              onChange={({ lat, lng }) => setSiteForm({ ...siteForm, lat, lng })}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Latitude</Label>
                <Input
                  className="mt-1.5 font-mono"
                  type="number"
                  step="any"
                  placeholder="51.5074"
                  value={siteForm.lat ?? ''}
                  onChange={e => setSiteForm({ ...siteForm, lat: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  data-testid="new-site-lat-input"
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">Longitude</Label>
                <Input
                  className="mt-1.5 font-mono"
                  type="number"
                  step="any"
                  placeholder="-0.1278"
                  value={siteForm.lng ?? ''}
                  onChange={e => setSiteForm({ ...siteForm, lng: e.target.value === '' ? null : parseFloat(e.target.value) })}
                  data-testid="new-site-lng-input"
                />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-500">Geofence radius (metres)</Label>
              <Input
                className="mt-1.5"
                type="number"
                min="50"
                max="5000"
                step="50"
                value={siteForm.geofenceMeters ?? 250}
                onChange={e => setSiteForm({ ...siteForm, geofenceMeters: parseInt(e.target.value) || 250 })}
                data-testid="new-site-geofence-input"
              />
              <div className="text-[11px] text-slate-500 mt-1">
                Engineers more than this far from the site coordinates will need to type a reason to clock in. Default 250m covers most industrial sites with car parks.
              </div>
            </div>
            <div>
              <Label>Customer</Label>
              <Select value={siteForm.customerId || 'none'} onValueChange={v => setSiteForm({ ...siteForm, customerId: v === 'none' ? '' : v })}>
                <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select customer..." /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— No customer —</SelectItem>
                  {customers.map(c => <SelectItem key={c.id} value={c.id}>{c.company || c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSiteOpen(false)}>Cancel</Button>
            <Button onClick={saveSite} className="bg-slate-900 hover:bg-slate-800" data-testid="save-site-btn">Add site</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      </>
      )}

      {/* Customer "Report an issue" dialog */}
      <Dialog open={!!reportAsset} onOpenChange={(o) => !o && setReportAsset(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Report an issue</DialogTitle>
            <DialogDescription>
              {reportAsset
                ? <>About <span className="font-semibold text-slate-900">{reportAsset.name}</span>{reportAsset.location ? <> at <span className="text-slate-700">{reportAsset.location}</span></> : null}{reportAsset.siteName ? <> · {reportAsset.siteName}</> : null}.</>
                : 'Tell us what went wrong and we\'ll get on it.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>What's the issue? *</Label>
              <Input
                className="mt-1.5"
                value={reportForm.title}
                onChange={e => setReportForm({ ...reportForm, title: e.target.value })}
                placeholder="Brief summary"
                data-testid="report-title-input"
              />
            </div>
            <div>
              <Label>Details</Label>
              <Textarea
                className="mt-1.5"
                rows={4}
                value={reportForm.description}
                onChange={e => setReportForm({ ...reportForm, description: e.target.value })}
                placeholder="When did it start? Any noises, leaks, error messages?"
                data-testid="report-description-input"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={reportForm.priority} onValueChange={v => setReportForm({ ...reportForm, priority: v })}>
                <SelectTrigger className="mt-1.5" data-testid="report-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low — when convenient</SelectItem>
                  <SelectItem value="medium">Medium — this week</SelectItem>
                  <SelectItem value="high">High — urgent</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportAsset(null)} disabled={reportSubmitting}>Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={reportSubmitting}
              className="bg-slate-900 hover:bg-slate-800"
              data-testid="submit-report-btn"
            >
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk import CSV dialog (admin only) */}
      <BulkImportAssetsDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onImported={() => { refresh(); }}
      />
    </div>
  );
};

export default Assets;
